import React from "react";
import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import logo from "../../assets/images/company-logo.png";
import stamp from "../../assets/images/paymentReceiptStamp.png";
import { numberToWordsINR } from "../../utils/commissionPDFUtils";
import Logo from "../../assets/images/FrelitFevicone.png";

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: "Helvetica",
    fontSize: 11,
    color: "#000",
  },

  header: {
    alignItems: "center",
    marginBottom: 20,
  },
  logo: {
    width: 120,
    height: 60,
    marginBottom: 8,
  },
  companyName: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 4,
  },

  titleCenter: {
    fontSize: 14,
    fontWeight: "bold",
    letterSpacing: 1,
    textAlign: "center",
    marginTop: 10,
  },

  dateRow: {
    alignItems: "flex-end",
    marginTop: 6,
    marginBottom: 16,
    fontSize: 12,
  },

  divider: {
    borderBottomWidth: 1,
    borderBottomColor: "#999",
    marginVertical: 10,
  },

  label: {
    fontWeight: "bold",
    fontSize: 14,
  },

  amountRow: {
    marginTop: 10,
    paddingBottom: 8,
    paddingTop: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    borderTopWidth: 1,
    borderTopColor: "#000",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  labelSize: {
    fontSize: 14,
  },
  amount: {
    fontSize: 14,
    fontWeight: "bold",
  },

  wordsRow: {
    marginTop: 12,
    flexDirection: "row",
  },

  words: {
    fontSize: 14,
    fontStyle: "italic",
  },

  wordsValue: {
    fontSize: 12,
    fontStyle: "italic",
    fontWeight: "bold",
  },

  purpose: {
    marginTop: 8,
    fontSize: 12,
  },

  stampImage: {
    width: 120,
    objectFit: "contain",
    marginBottom: 6,
  },

  signatureBlock: {
    marginTop: 60,
    alignItems: "flex-start",
  },

  signatureText: {
    fontSize: 9,
    color: "#333",
    textAlign: "center",
  },
  forText: {
    fontSize: 10,
    marginBottom: 4,
    fontWeight: "bold",
  },

  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    fontSize: 8,
    textAlign: "center",
    color: "#666",
  },
  watermark: {
    position: "absolute",
    top: 250,
    left: 150,
    width: 300,
    height: 300,
    opacity: 0.03,
  },
  termsContainer: {
    marginTop: 160,
    fontSize: 7,
    lineHeight: 1.6,
    color: "#333",
  },

  termsTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 6,
    textDecoration: "underline",
  },
});

interface Props {
  paymentData: {
    customerName: string;
    paymentAmount: number;
    paymentDate: string;
    coApplicantName?: string;
  };
  generatedDate: string;
}

const TERMS_TEXT = [
  "This receipt is issued as a financial acknowledgment of funds received. A formal Tax Invoice will be issued separately upon the relevant billing milestone.",
  "All payments made via Cheque, Draft, or Online Transfer (NEFT/RTGS/IMPS) are credited subject to successful realization in the bank account of Frelit Energy Pvt Ltd.",
  "Payments categorized as Loan Processing Fees for Residential/Commercial are collected for administrative and documentation purposes.",
  "Amounts received as Residential/Commercial Loan Downpayments represent the customer’s mandatory margin contribution and are locked to the specific customer/project/loan application.",
  "Payments are processed based on applicable GST rates. Any statutory revision at the time of final invoicing shall be adjusted accordingly.",
  "Booking amounts and advance payments are non-refundable once the financial or technical procurement process has been initiated.",
  "Customers are advised to make payments only to the official bank account of Frelit Energy Pvt Ltd. The company is not responsible for cash payments made to unauthorized individuals.",
];

export const PaymentReceiptPDF: React.FC<Props> = ({ paymentData }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Image src={Logo} style={styles.watermark} fixed />

        {/* HEADER */}
        <View style={styles.header}>
          <Image src={logo} style={styles.logo} />
          <Text style={styles.companyName}>FRELIT ENERGY PVT LTD</Text>
          <Text>GST No: 24AADCF7701R1ZB</Text>
        </View>

        {/* TITLE + META */}
        <Text style={styles.titleCenter}>PAYMENT ACKNOWLEDGEMENT</Text>

        <View style={styles.dateRow}>
          <Text>Date: {paymentData.paymentDate}</Text>
        </View>

        <Text style={styles.purpose}>
          <Text style={styles.labelSize}>Received from:{"  "}</Text>

          <Text style={styles.label}>
            {paymentData.customerName}
            {paymentData.coApplicantName
              ? ` / ${paymentData.coApplicantName}`
              : ""}
          </Text>
        </Text>

        {/* DETAILS */}
        <View style={styles.amountRow}>
          <Text style={styles.labelSize}>Amount Received:</Text>
          <Text style={styles.amount}>
            INR {paymentData.paymentAmount.toLocaleString("en-IN")}
          </Text>
        </View>

        <View style={styles.wordsRow}>
          <Text style={styles.words}>Amount in Words: </Text>
        </View>
        <View style={styles.wordsRow}>
          <Text style={styles.wordsValue}>
            {numberToWordsINR(paymentData.paymentAmount, {
              prefix: "Rupees",
              suffix: "Only",
            })}
          </Text>
        </View>

        {/* SIGNATURE STAMP */}
        <View style={styles.signatureBlock}>
          <View style={{ alignItems: "flex-start" }}>
            <Text style={styles.forText}>FRELIT ENERGY PVT LTD</Text>

            <View style={{ alignItems: "center", marginTop: 6 }}>
              <Image src={stamp} style={styles.stampImage} />
              <Text style={styles.signatureText}>Authorized Signatory</Text>
            </View>
          </View>
        </View>

        <View style={styles.termsContainer}>
          <Text style={styles.termsTitle}>Terms & Conditions</Text>

          {TERMS_TEXT.map((item, index) => (
            <Text key={index}>• {item}</Text>
          ))}
        </View>

        {/* FOOTER */}
        <View style={styles.footer}>
          <Text>This is a computer-generated receipt</Text>
        </View>
      </Page>
    </Document>
  );
};
